import { useEffect, useRef, useState, memo } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PlatformMapProps {
  center: [number, number] | null;
  userLocation?: [number, number] | null; // Actual user GPS position for marker
  routePoints?: [number, number][]; // Array of [lat, lng] for route polyline (or use routeSegments)
  routeSegments?: [number, number][][]; // Segments for route (no line between segments - e.g. pause/resume)
  showUserMarker?: boolean;
  showRoute?: boolean;
  showStartFinishMarkers?: boolean; // Show start (green) and finish (red) pin markers
  showKmMarkers?: boolean; // Show 1km, 2km, etc. markers along the route
  className?: string;
  darkMode?: boolean;
  zoom?: number;
  interactive?: boolean;
  fitToRoute?: boolean; // Auto-fit map bounds to show entire route
  fitPaddingBottom?: number; // Extra padding at bottom (e.g. 1.5) shifts route up to center it in portrait map
  onMapClick?: () => void;
  onUserScroll?: () => void; // Called when user manually scrolls/pans the map
  autoFollow?: boolean; // When true, map follows user location automatically
}

// Declare MapKit types
declare global {
  interface Window {
    mapkit: any;
    _mapkitToken: string | null;
  }
}

// Global state
let mapkitScriptLoaded = false;
let mapkitInitialized = false;

/**
 * PlatformMap - Real Apple Maps using MapKit JS
 */
const PlatformMapComponent = ({ 
  center, 
  userLocation,
  routePoints = [],
  routeSegments,
  showUserMarker = true,
  showRoute = true,
  showStartFinishMarkers = false,
  showKmMarkers = false,
  className = "",
  darkMode = true,
  zoom = 15,
  interactive = true,
  fitToRoute = false,
  fitPaddingBottom = 1,
  onMapClick,
  onUserScroll,
  autoFollow = true
}: PlatformMapProps) => {
  const mapLat = center ? center[0] : 59.3293;
  const mapLng = center ? center[1] : 18.0686;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const userMarkerRef = useRef<any>(null);
  const routeOverlayRef = useRef<any>(null);
  const startFinishMarkersRef = useRef<any[]>([]);
  const kmMarkersRef = useRef<any[]>([]);
  const [containerReady, setContainerReady] = useState(false);
  const [mapkitReady, setMapkitReady] = useState(mapkitInitialized);
  const [mapCreated, setMapCreated] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const lastUserScrollTime = useRef<number>(0);
  const isAnimating = useRef<boolean>(false);
  
  
  // Track when container is mounted
  useEffect(() => {
    if (containerRef.current) {
      console.log('[MapKit] Container ready');
      setContainerReady(true);
    }
  }, []);
  
  // Load and initialize MapKit JS
  useEffect(() => {
    let isMounted = true;
    
    const initMapKit = async () => {
      try {
        // Step 1: Load script
        if (!mapkitScriptLoaded && !window.mapkit) {
          console.log('[MapKit] Loading script...');
          await new Promise<void>((resolve, reject) => {
            const script = document.createElement('script');
            script.src = 'https://cdn.apple-mapkit.com/mk/5.x.x/mapkit.js';
            script.crossOrigin = 'anonymous';
            script.async = true;
            script.onload = () => {
              console.log('[MapKit] Script loaded');
              mapkitScriptLoaded = true;
              resolve();
            };
            script.onerror = () => reject(new Error('Failed to load script'));
            document.head.appendChild(script);
          });
        }
        
        // Step 2: Initialize MapKit (once globally)
        if (!mapkitInitialized && window.mapkit) {
          console.log('[MapKit] Fetching token...');
          const { data, error } = await supabase.functions.invoke('generate-mapkit-token');
          
          if (error || !data?.token) {
            throw new Error('Failed to get token');
          }
          
          console.log('[MapKit] Token received, length:', data.token.length);
          window._mapkitToken = data.token;
          
          window.mapkit.init({
            authorizationCallback: (done: (token: string) => void) => {
              console.log('[MapKit] Auth callback called');
              done(data.token);
            }
          });
          
          mapkitInitialized = true;
          console.log('[MapKit] Initialization complete');
        }
        
        if (isMounted) {
          setMapkitReady(true);
        }
        
      } catch (err: any) {
        console.error('[MapKit] Init error:', err?.message || err);
        if (isMounted) {
          setLoadError(err?.message || 'Failed to initialize');
        }
      }
    };
    
    initMapKit();
    
    return () => { isMounted = false; };
  }, []);
  
  // Create map when both container and MapKit are ready.
  //
  // IMPORTANT: this effect must NOT depend on mapLat/mapLng or it will re-run
  // every GPS tick and flood the logs with "[MapKit] Waiting..." while also
  // doing pointless equality checks ~once per second. We only need the initial
  // center; subsequent center updates happen in the dedicated `center` effect
  // below.
  useEffect(() => {
    if (mapCreated) return;
    if (!containerReady || !mapkitReady || !containerRef.current) return;

    if (!window.mapkit) {
      console.error('[MapKit] window.mapkit not available');
      return;
    }

    try {
      console.log('[MapKit] Creating map at:', mapLat, mapLng);
      
      // Destroy existing map if any
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.destroy(); } catch (e) {}
        mapInstanceRef.current = null;
      }
      
      // Calculate initial region - use fitToRoute bounds if available, otherwise default
      let initialCenter = new window.mapkit.Coordinate(mapLat, mapLng);
      let initialSpan = new window.mapkit.CoordinateSpan(0.008, 0.008);
      
      if (fitToRoute && routePoints && routePoints.length >= 2) {
        // Calculate bounds from route points for correct initial zoom
        const lats = routePoints.map(p => p[0]);
        const lngs = routePoints.map(p => p[1]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        const latPadding = Math.max((maxLat - minLat) * 0.5, 0.003);
        const lngPadding = Math.max((maxLng - minLng) * 0.5, 0.003);
        
        initialCenter = new window.mapkit.Coordinate((minLat + maxLat) / 2, (minLng + maxLng) / 2);
        initialSpan = new window.mapkit.CoordinateSpan(
          maxLat - minLat + latPadding * 2,
          maxLng - minLng + lngPadding * 2
        );
        console.log('[MapKit] Using fitToRoute initial region');
      }
      
      const map = new window.mapkit.Map(containerRef.current, {
        center: initialCenter,
        showsUserLocation: false, // Disabled - we use Capacitor GPS + custom green dot overlay
        showsUserLocationControl: false,
        showsCompass: window.mapkit.FeatureVisibility.Hidden,
        showsZoomControl: false,
        showsMapTypeControl: false,
        colorScheme: darkMode 
          ? window.mapkit.Map.ColorSchemes.Dark 
          : window.mapkit.Map.ColorSchemes.Light,
        mapType: window.mapkit.Map.MapTypes.Standard,
        isZoomEnabled: interactive,
        isScrollEnabled: interactive,
        isRotationEnabled: false,
      });
      
      // Set initial region
      const region = new window.mapkit.CoordinateRegion(initialCenter, initialSpan);
      map.setRegionAnimated(region, false);
      
      // Detect user scroll/pan interactions
      map.addEventListener('scroll-start', () => {
        if (!isAnimating.current) {
          lastUserScrollTime.current = Date.now();
          onUserScroll?.();
        }
      });
      
      map.addEventListener('scroll-end', () => {
        if (!isAnimating.current && Date.now() - lastUserScrollTime.current < 500) {
          onUserScroll?.();
        }
      });
      
      // Also detect drag (pan) interactions
      map.addEventListener('drag-start', () => {
        if (!isAnimating.current) {
          lastUserScrollTime.current = Date.now();
          onUserScroll?.();
        }
      });
      
      mapInstanceRef.current = map;
      setMapCreated(true);
      console.log('[MapKit] Map created successfully!');
      
    } catch (err: any) {
      console.error('[MapKit] Map creation error:', err?.message || err);
      setLoadError('Failed to create map');
    }
    // Intentionally only re-run when readiness flags flip. mapLat/mapLng/etc
    // are read at creation time; live updates flow through the other effects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerReady, mapkitReady, mapCreated]);
  
  // Update map center when position changes (only if autoFollow is enabled)
  useEffect(() => {
    if (!mapInstanceRef.current || !center || !mapCreated) return;
    
    // Only auto-center if autoFollow is enabled
    if (!autoFollow) return;
    
    try {
      isAnimating.current = true;
      const newCenter = new window.mapkit.Coordinate(center[0], center[1]);
      mapInstanceRef.current.setCenterAnimated(newCenter, true);
      
      // Reset animation flag after animation completes
      setTimeout(() => {
        isAnimating.current = false;
      }, 500);
    } catch (err) {
      isAnimating.current = false;
    }
  }, [center, mapCreated, autoFollow]);
  
  // Update color scheme
  useEffect(() => {
    if (!mapInstanceRef.current || !mapCreated) return;
    
    try {
      mapInstanceRef.current.colorScheme = darkMode 
        ? window.mapkit.Map.ColorSchemes.Dark 
        : window.mapkit.Map.ColorSchemes.Light;
    } catch (err) {
      // Ignore
    }
  }, [darkMode, mapCreated]);
  
  // Update user location marker (custom green dot - INSTANT position updates to stay in sync with route line)
  useEffect(() => {
    if (!mapInstanceRef.current || !mapCreated || !window.mapkit) return;
    
    const location = userLocation || center;
    if (!location || !showUserMarker) {
      // Remove marker if no location
      if (userMarkerRef.current) {
        try { mapInstanceRef.current.removeAnnotation(userMarkerRef.current); } catch (e) {}
        userMarkerRef.current = null;
      }
      return;
    }
    
    try {
      const [lat, lng] = location;
      
      if (userMarkerRef.current) {
        // INSTANT update - no interpolation to ensure perfect sync with route line
        userMarkerRef.current.coordinate = new window.mapkit.Coordinate(lat, lng);
      } else {
        // Create custom dot annotation - 50% bigger (24px instead of 16px)
        const dotElement = document.createElement('div');
        dotElement.className = 'user-location-marker';
        dotElement.innerHTML = `
          <div style="
            position: relative;
            width: 24px; 
            height: 24px;
          ">
            <div style="
              position: absolute;
              width: 54px;
              height: 54px;
              top: -15px;
              left: -15px;
              background: radial-gradient(circle, rgba(11, 217, 168, 0.4) 0%, rgba(11, 217, 168, 0) 70%);
              border-radius: 50%;
              animation: smoothPulse 2.5s ease-in-out infinite;
            "></div>
            <div style="
              position: absolute;
              top: 0;
              left: 0;
              width: 24px;
              height: 24px;
              background: linear-gradient(135deg, #0bd9a8 0%, #b6fced 100%);
              border-radius: 50%;
              border: 3px solid white;
              box-shadow: 0 2px 12px rgba(11, 217, 168, 0.6), 0 0 0 1px rgba(11, 217, 168, 0.15);
            "></div>
          </div>
          <style>
            @keyframes smoothPulse {
              0%, 100% { transform: scale(1); opacity: 0.8; }
              50% { transform: scale(1.4); opacity: 0.2; }
            }
          </style>
        `;
        
        const coordinate = new window.mapkit.Coordinate(lat, lng);
        const annotation = new window.mapkit.Annotation(coordinate, () => dotElement, {
          // Negative Y moves dot DOWN so line goes through center of 24px dot
          anchorOffset: new DOMPoint(0, -12),
          displayPriority: 1000, // Render on top of route line
        });
        
        mapInstanceRef.current.addAnnotation(annotation);
        userMarkerRef.current = annotation;
      }
    } catch (err) {
      console.warn('[MapKit] Failed to update user marker:', err);
    }
  }, [userLocation, center, mapCreated, showUserMarker]);
  
  // Update route polyline with gradient effect
  useEffect(() => {
    if (!mapInstanceRef.current || !mapCreated || !window.mapkit) return;
    
    // Remove old route
    if (routeOverlayRef.current) {
      try { 
        if (Array.isArray(routeOverlayRef.current)) {
          routeOverlayRef.current.forEach(overlay => {
            try { mapInstanceRef.current.removeOverlay(overlay); } catch (e) {}
          });
        } else {
          mapInstanceRef.current.removeOverlay(routeOverlayRef.current); 
        }
      } catch (e) {}
      routeOverlayRef.current = null;
    }
    
    const segments = routeSegments && routeSegments.length > 0
      ? routeSegments.filter(seg => seg.length >= 2)
      : (routePoints && routePoints.length >= 2 ? [routePoints] : []);
    
    if (!showRoute || segments.length === 0) return;
    
    try {
      const allOverlays: any[] = [];
      for (const seg of segments) {
        const coordinates = seg.map(point => new window.mapkit.Coordinate(point[0], point[1]));
        const glowLine = new window.mapkit.PolylineOverlay(coordinates, {
          style: new window.mapkit.Style({
          lineWidth: 18,
          lineJoin: 'round',
          lineCap: 'round',
          strokeColor: '#0bd9a8',
              strokeOpacity: 0.25,
          }),
        });
      
      // Main route line (primary green gradient color)
      const mainLine = new window.mapkit.PolylineOverlay(coordinates, {
        style: new window.mapkit.Style({
          lineWidth: 7.5,
          lineJoin: 'round',
          lineCap: 'round',
          strokeColor: '#0bd9a8', // Primary teal/mint green
          strokeOpacity: 0.95,
        }),
      });
      
      // Inner highlight line (lighter, creates gradient effect)
      const highlightLine = new window.mapkit.PolylineOverlay(coordinates, {
        style: new window.mapkit.Style({
          lineWidth: 3,
          lineJoin: 'round',
          lineCap: 'round',
          strokeColor: '#b6fced', // Lighter mint - matches gradient end
          strokeOpacity: 0.6,
        }),
      });
      
        allOverlays.push(glowLine, mainLine, highlightLine);
        mapInstanceRef.current.addOverlay(glowLine);
        mapInstanceRef.current.addOverlay(mainLine);
        mapInstanceRef.current.addOverlay(highlightLine);
      }
      routeOverlayRef.current = allOverlays;
      
      const allPoints = segments.flat();
      // Fit map to show entire route if requested (with generous padding)
      if (fitToRoute && allPoints.length >= 2) {
        const lats = allPoints.map(p => p[0]);
        const lngs = allPoints.map(p => p[1]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        // Add generous padding - zoom out so entire route is visible
        const latPadding = Math.max((maxLat - minLat) * 0.6, 0.005);
        const lngPadding = Math.max((maxLng - minLng) * 0.6, 0.005);
        const topPadding = latPadding;
        const bottomPadding = latPadding * fitPaddingBottom;
        const north = maxLat + topPadding;
        const south = minLat - bottomPadding;
        const centerLat = (north + south) / 2;
        const spanLat = north - south;
        
        const region = new window.mapkit.CoordinateRegion(
          new window.mapkit.Coordinate(centerLat, (minLng + maxLng) / 2),
          new window.mapkit.CoordinateSpan(
            spanLat,
            maxLng - minLng + lngPadding * 2
          )
        );
        
        mapInstanceRef.current.setRegionAnimated(region, false);
      }
      
      console.log('[MapKit] Route updated with', segments.length, 'segment(s),', segments.reduce((a, s) => a + s.length, 0), 'points');
    } catch (err) {
      console.warn('[MapKit] Failed to update route:', err);
    }
  }, [routePoints, routeSegments, mapCreated, showRoute, fitToRoute, fitPaddingBottom]);
  
  // Ensure fitToRoute is applied after map is ready (applies multiple times to ensure it sticks)
  const pointsForFit = (routeSegments && routeSegments.length > 0 ? routeSegments.flat() : routePoints) || [];
  useEffect(() => {
    if (!mapInstanceRef.current || !mapCreated || !window.mapkit) return;
    if (!fitToRoute || !pointsForFit || pointsForFit.length < 2) return;
    
    const applyFitToRoute = () => {
      if (!mapInstanceRef.current) return;
      
      try {
        const lats = pointsForFit.map(p => p[0]);
        const lngs = pointsForFit.map(p => p[1]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);
        
        const latPadding = Math.max((maxLat - minLat) * 0.6, 0.005);
        const lngPadding = Math.max((maxLng - minLng) * 0.6, 0.005);
        const topPadding = latPadding;
        const bottomPadding = latPadding * fitPaddingBottom;
        const north = maxLat + topPadding;
        const south = minLat - bottomPadding;
        const centerLat = (north + south) / 2;
        const spanLat = north - south;
        
        const region = new window.mapkit.CoordinateRegion(
          new window.mapkit.Coordinate(centerLat, (minLng + maxLng) / 2),
          new window.mapkit.CoordinateSpan(
            spanLat,
            maxLng - minLng + lngPadding * 2
          )
        );
        
        mapInstanceRef.current.setRegionAnimated(region, false);
      } catch (err) {
        console.warn('[MapKit] Failed to apply fitToRoute:', err);
      }
    };
    
    // Apply fitToRoute multiple times with increasing delays to ensure it sticks after map settles
    const timeouts = [
      setTimeout(applyFitToRoute, 50),
      setTimeout(applyFitToRoute, 200),
      setTimeout(applyFitToRoute, 500),
      setTimeout(applyFitToRoute, 800),
    ];
    
    console.log('[MapKit] Scheduling fitToRoute applications');
    
    return () => timeouts.forEach(t => clearTimeout(t));
  }, [mapCreated, fitToRoute, fitPaddingBottom, routePoints, routeSegments]);
  
  // Helper function to calculate distance between two points (Haversine)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;
    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Add start/finish AND km markers together - permanent like each other, re-add after fitToRoute settles
  const addAllMarkers = () => {
    if (!mapInstanceRef.current || !window.mapkit) return;
    // Remove old markers
    startFinishMarkersRef.current.forEach(marker => {
      try { mapInstanceRef.current.removeAnnotation(marker); } catch (e) {}
    });
    startFinishMarkersRef.current = [];
    kmMarkersRef.current.forEach(marker => {
      try { mapInstanceRef.current.removeAnnotation(marker); } catch (e) {}
    });
    kmMarkersRef.current = [];
    const pointsForMarkers = (routeSegments && routeSegments.length > 0 ? routeSegments.flat() : routePoints) || [];
    if (!pointsForMarkers || pointsForMarkers.length < 2) return;
    
    const segs = routeSegments && routeSegments.length > 0 ? routeSegments : [routePoints!];
    
    try {
      // Start/finish markers
      if (showStartFinishMarkers) {
      const startPoint = pointsForMarkers[0];
      const startElement = document.createElement('div');
      startElement.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        ">
          <div style="
            background: #22c55e;
            color: white;
            font-size: 9px;
            font-weight: 600;
            padding: 3px 6px;
            border-radius: 4px;
            white-space: nowrap;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          ">Start</div>
          <div style="
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-top: 6px solid #22c55e;
            margin-top: -1px;
          "></div>
        </div>
      `;
      
      const startAnnotation = new window.mapkit.Annotation(
        new window.mapkit.Coordinate(startPoint[0], startPoint[1]),
        () => startElement,
        { anchorOffset: new DOMPoint(0, 10), displayPriority: 900 } // Pointer tip touches line
      );
      
      // Finish marker - text label with pointer (positioned ABOVE line)
      const endPoint = pointsForMarkers[pointsForMarkers.length - 1];
      const endElement = document.createElement('div');
      endElement.innerHTML = `
        <div style="
          display: flex;
          flex-direction: column;
          align-items: center;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        ">
          <div style="
            background: #ef4444;
            color: white;
            font-size: 9px;
            font-weight: 600;
            padding: 3px 6px;
            border-radius: 4px;
            white-space: nowrap;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          ">Finish</div>
          <div style="
            width: 0;
            height: 0;
            border-left: 5px solid transparent;
            border-right: 5px solid transparent;
            border-top: 6px solid #ef4444;
            margin-top: -1px;
          "></div>
        </div>
      `;
      
      const endAnnotation = new window.mapkit.Annotation(
        new window.mapkit.Coordinate(endPoint[0], endPoint[1]),
        () => endElement,
        { anchorOffset: new DOMPoint(0, 10), displayPriority: 901 } // Pointer tip touches line
      );
      
      mapInstanceRef.current.addAnnotation(startAnnotation);
      mapInstanceRef.current.addAnnotation(endAnnotation);
      startFinishMarkersRef.current = [startAnnotation, endAnnotation];
      console.log('[MapKit] Start/Finish pins added at', startPoint, endPoint);
      }
      
      // Km markers - black, every 1km, 2km, 3km... (same style as before, larger for visibility)
      if (showKmMarkers) {
        let cumulativeDistance = 0;
        let nextKmMark = 1000;
        const kmPoints: { km: number; lat: number; lng: number }[] = [];
        for (const seg of segs) {
          if (seg.length < 2) continue;
          for (let i = 1; i < seg.length; i++) {
            const prevPoint = seg[i - 1];
            const currentPoint = seg[i];
            const segmentDistance = calculateDistance(
              prevPoint[0], prevPoint[1],
              currentPoint[0], currentPoint[1]
            );
            if (segmentDistance < 0.1) continue;
            const prevCumulative = cumulativeDistance;
            cumulativeDistance += segmentDistance;
            while (cumulativeDistance >= nextKmMark) {
              const ratio = (nextKmMark - prevCumulative) / segmentDistance;
              const kmLat = prevPoint[0] + (currentPoint[0] - prevPoint[0]) * ratio;
              const kmLng = prevPoint[1] + (currentPoint[1] - prevPoint[1]) * ratio;
              kmPoints.push({ km: nextKmMark / 1000, lat: kmLat, lng: kmLng });
              nextKmMark += 1000;
            }
          }
        }
        kmPoints.forEach(({ km, lat, lng }) => {
          const kmElement = document.createElement('div');
          kmElement.innerHTML = `
            <div style="display:flex;flex-direction:column;align-items:center">
              <div style="background:rgba(0,0,0,0.9);color:white;font-size:10px;font-weight:700;padding:3px 6px;border-radius:4px;white-space:nowrap;border:2px solid rgba(255,255,255,0.6);box-shadow:0 2px 6px rgba(0,0,0,0.5)">${km}km</div>
              <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:8px solid rgba(0,0,0,0.9);margin-top:-1px"></div>
            </div>`;
          const annotation = new window.mapkit.Annotation(
            new window.mapkit.Coordinate(lat, lng),
            () => kmElement,
            { anchorOffset: new DOMPoint(0, 10), displayPriority: 902 }
          );
          mapInstanceRef.current.addAnnotation(annotation);
          kmMarkersRef.current.push(annotation);
        });
        console.log('[MapKit] Added', kmPoints.length, 'km markers');
      }
    } catch (err) {
      console.warn('[MapKit] Failed to add markers:', err);
    }
  };
  useEffect(() => {
    if (!mapCreated) return;
    if (!showStartFinishMarkers && !showKmMarkers) return;
    addAllMarkers();
    // Re-add after fitToRoute settles - multiple delays to prevent flicker when region animates
    if (fitToRoute && (showStartFinishMarkers || showKmMarkers)) {
      const t1 = setTimeout(addAllMarkers, 400);
      const t2 = setTimeout(addAllMarkers, 900);
      const t3 = setTimeout(addAllMarkers, 1500); // Final re-add after all map settling
      return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
    }
  }, [routePoints, routeSegments, mapCreated, showStartFinishMarkers, showKmMarkers, fitToRoute]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (mapInstanceRef.current) {
        try { mapInstanceRef.current.destroy(); } catch (e) {}
        mapInstanceRef.current = null;
      }
    };
  }, []);
  
  // Error fallback to Google Maps
  if (loadError) {
    return (
      <div className={`${className} relative overflow-hidden`}>
        <iframe
          title="Map"
          width="100%"
          height="100%"
          style={{ 
            border: 0, 
            filter: darkMode ? 'brightness(0.75) contrast(1.1) saturate(0.9)' : 'none' 
          }}
          loading="lazy"
          src={`https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d2000!2d${mapLng}!3d${mapLat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sse!4v1700000000000!5m2!1sen!2sse`}
          allowFullScreen
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    );
  }
  
  // Always render the container - MapKit will draw into it
  return (
    <div 
      ref={containerRef}
      className={`${className} relative`}
      onClick={onMapClick}
      style={{ minHeight: '200px', minWidth: '200px' }}
    >
      {/* Loading overlay */}
      {!mapCreated && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <div className="text-sm text-muted-foreground">Loading Apple Maps...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export const PlatformMap = memo(PlatformMapComponent);
